import { isOidcRoute } from "~/utils/helpers/auth/oidc-contract";

export const RETURN_TO_STORAGE_KEY = "simbel_auth_return_to";

export interface IReturnToStorage {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

interface ReturnToSelection {
  origin: string;
  explicit: {
    provided: boolean;
    value?: unknown;
  };
  stored?: unknown;
  ssoRedirect?: unknown;
  redirectUrl?: unknown;
}

interface IPostLoginDestinationOptions {
  returnTo?: string;
  profileComplete: boolean;
  userId: string;
  isAllowed: boolean;
  isKnownRoute(destination: string): boolean;
  fetchRequestOwnerId(id: string): Promise<string | undefined>;
  fetchTrainingPlanOwnerId(id: string): Promise<string | undefined>;
}

export interface IPostLoginDestination {
  destination: string;
  consumeReturnTo: boolean;
}

const AUTH_LOOP_PATHS = new Set(["/", "/sso/redirect", "/sso/callback", "/oauth/auto-login"]);
const ABSOLUTE_URL_PATTERN = /^[a-z][a-z\d+.-]*:/i;
const BUSINESS_ALIAS_PATTERN = /^\/training-plan\/(tr-)?([a-z\d_-]+)$/i;

function hasControlCharacters(value: string): boolean {
  return [...value].some((character) => {
    const code = character.charCodeAt(0);
    return code <= 31 || code === 127;
  });
}

function decodePath(path: string): string | undefined {
  let decoded = path;

  for (let index = 0; index < 10; index += 1) {
    if (decoded.includes("\\") || decoded.startsWith("//")) {
      return undefined;
    }

    try {
      const next = decodeURIComponent(decoded);
      if (next === decoded) {
        return decoded;
      }
      decoded = next;
    } catch {
      return undefined;
    }
  }

  return undefined;
}

export function normalizeReturnTo(value: unknown, origin: string): string | undefined {
  if (typeof value !== "string") {
    return undefined;
  }

  const candidate = value.trim();
  if (!candidate || candidate.toLowerCase() === "none" || candidate.includes("\\") || hasControlCharacters(candidate)) {
    return undefined;
  }

  try {
    const expectedOrigin = new URL(origin).origin;
    const isAbsolute = ABSOLUTE_URL_PATTERN.test(candidate);

    if (candidate.startsWith("//")) {
      return undefined;
    }

    const parsed = new URL(isAbsolute ? candidate : candidate.startsWith("/") ? candidate : `/${candidate}`, origin);

    if (!["http:", "https:"].includes(parsed.protocol) || parsed.origin !== expectedOrigin) {
      return undefined;
    }

    const decodedPath = decodePath(parsed.pathname);
    if (!decodedPath || isOidcRoute(decodedPath) || AUTH_LOOP_PATHS.has(decodedPath.replace(/\/+$/, "") || "/")) {
      return undefined;
    }

    return `${parsed.pathname}${parsed.search}${parsed.hash}`;
  } catch {
    return undefined;
  }
}

export function selectReturnTo(selection: ReturnToSelection): string | undefined {
  if (selection.explicit.provided) {
    return normalizeReturnTo(selection.explicit.value, selection.origin) ?? "/home";
  }

  return [selection.stored, selection.ssoRedirect, selection.redirectUrl]
    .map((candidate) => normalizeReturnTo(candidate, selection.origin))
    .find((candidate) => candidate !== undefined);
}

export function storeReturnTo(storage: IReturnToStorage, value: unknown, origin: string): string | undefined {
  const normalized = normalizeReturnTo(value, origin);

  if (normalized) {
    storage.setItem(RETURN_TO_STORAGE_KEY, normalized);
  } else {
    clearStoredReturnTo(storage);
  }

  return normalized;
}

export function readStoredReturnTo(storage: IReturnToStorage, origin: string): string | undefined {
  const normalized = normalizeReturnTo(storage.getItem(RETURN_TO_STORAGE_KEY), origin);

  if (!normalized) {
    clearStoredReturnTo(storage);
  }

  return normalized;
}

export function clearStoredReturnTo(storage: IReturnToStorage): void {
  storage.removeItem(RETURN_TO_STORAGE_KEY);
}

export function isBusinessReturnToAlias(value: string): boolean {
  return BUSINESS_ALIAS_PATTERN.test(new URL(value, "https://internal.invalid").pathname);
}

export async function resolvePostLoginDestination(
  options: IPostLoginDestinationOptions,
): Promise<IPostLoginDestination> {
  if (!options.profileComplete) {
    return { destination: "/", consumeReturnTo: false };
  }

  if (!options.returnTo) {
    return { destination: "/home", consumeReturnTo: true };
  }

  const businessAlias = BUSINESS_ALIAS_PATTERN.exec(new URL(options.returnTo, "https://internal.invalid").pathname);

  if (businessAlias) {
    const isTrainingPlan = businessAlias[1] === "tr-";
    const id = businessAlias[2];
    const ownerId = isTrainingPlan ? await options.fetchTrainingPlanOwnerId(id) : await options.fetchRequestOwnerId(id);

    if (ownerId && ownerId === options.userId) {
      return {
        destination: `/my-trainings?id=${id}&booking=${isTrainingPlan}`,
        consumeReturnTo: true,
      };
    }

    if (options.isAllowed) {
      return {
        destination: isTrainingPlan ? `/registrations?details_id=tr-${id}&type=session` : `/requests/${id}`,
        consumeReturnTo: true,
      };
    }

    return { destination: "/catalog", consumeReturnTo: true };
  }

  return {
    destination: options.isKnownRoute(options.returnTo) ? options.returnTo : "/home",
    consumeReturnTo: true,
  };
}
